import {Box, HStack, Image, VStack, useBreakpointValue} from '@chakra-ui/react';
import {CartButton} from './CartButton/CartButton';
import {PaniersBadge, StarIcon} from './NavButtonIcons';
import {ListePaniersModale} from '../Modale/ListePaniersModale';
import NiceModal from '@ebay/nice-modal-react';
import {MenuCatalog} from './MenuCatalog/MenuCatalog';
import {Searchbar} from './Searchbar/Searchbar';
import {
  routes,
  useLivraisonsPossiblesContext,
  useTotalPaniers,
} from 'ff-common';
import {useNavigate} from 'react-router-dom';
import {HeaderBand} from '../../domains/HeaderBand/HeaderBand';
import {CheckLoaded} from '@/containers/CheckLoaded';
import {useHalState} from '@synako/haligator';
import {useClient, useSocieteCommerciale} from '@/haligator-factory';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {NavButton} from './NavButton';
import {useTranslation} from 'react-i18next';
import {ListIcon} from '../svg/ListIcon';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';
import {PromotionIcon} from '../svg/PromotionIcon';
import {usePromoMpSearch} from 'ff-common';
import {NAVBAR_HEIGHT} from '@/constants/layout';

export const Navbar: React.FC = () => {
  const hide_mobile = {display: {base: 'none', md: 'flex'}};

  const navigate = useNavigate();

  const societeCommerciale = useHalState(useSocieteCommerciale());
  const totalPaniers = useTotalPaniers(useClient());

  const {buildClientRoute} = useClientRoute();
  const {t} = useTranslation();

  // Utiliser effectiveTemperature du contexte
  const {effectiveTemperature} = useLivraisonsPossiblesContext();

  // Passer effectiveTemperature à usePromoMpSearch
  const {itemResultsPromo, itemResultsMP, promoDispo} = usePromoMpSearch({
    temperature_de_livraison: effectiveTemperature,
  });

  // Label responsive pour le bouton paniers
  const paniersActifsLabel = t('panier.paniers_actifs', {count: totalPaniers});
  const paniersLabel =
    useBreakpointValue({
      base: t('panier.paniers', {count: totalPaniers}),
      lg: paniersActifsLabel,
    }) ?? paniersActifsLabel;

  const promoLien = buildClientRoute(
    routes.client.catalogue,
    {},
    {nature_du_prix: 'promo', from: 'promo'},
  );
  const MPLien = buildClientRoute(
    routes.client.catalogue,
    {},
    {est_MP: true, from: 'mp'},
  );

  const buttonText = promoDispo
    ? t('catalogue.promotions')
    : t('catalogue.indispensables');
  const Lien = promoDispo ? promoLien : MPLien;

  return (
    <VStack
      gap='0'
      w='100%'
      top='0'
      position='sticky'
      zIndex='sticky'
    >
      <HeaderBand />
      <HStack
        w='100%'
        h={NAVBAR_HEIGHT}
        alignItems='center'
        py={{base: '0', md: '3'}}
        pl={{base: '0', md: '4', xl: '30px'}}
        pr='0'
        gap='0'
        // Garde-fou : rien ne peut provoquer de scroll horizontal de la page.
        // `clip` (et non `hidden`) pour ne pas créer de contexte de scroll et
        // laisser les dépassements verticaux (badges) visibles.
        overflowX='clip'
        bg='white'
        boxShadow='0 2px 6px rgba(0, 0, 0, 0.06)'
        borderBottom='1px solid'
        borderColor='gray.light'
      >
        {/* Partie gauche : logo, catalogue, recherche, nav buttons */}
        <HStack
          flex='1'
          alignItems='center'
          gap={{base: '1', md: '1', lg: '2', xl: '4'}}
          overflow='visible'
          minW='0'
          pr='2'
        >
          <HStack
            alignItems='stretch'
            maxHeight='100%'
            mr={{base: '20px', lg: '1', xl: '20px'}}
            cursor='pointer'
            _hover={{opacity: '30%'}}
            onClick={() => {
              navigate(buildClientRoute(routes.client.accueil));
            }}
            {...hide_mobile}
          >
            <CheckLoaded
              hook={societeCommerciale}
              render={({data}) => {
                return (
                  <Image
                    src={imageOptimisee(data.url_logo, 192)}
                    alt={data.tolede.nom_commercial}
                    htmlWidth='464'
                    htmlHeight='294'
                    maxW='6em'
                    maxH='51px'
                    objectFit='contain'
                    margin='-1'
                  />
                );
              }}
            />
          </HStack>
          <MenuCatalog props={hide_mobile} />
          <Searchbar
            props={{display: {base: 'none', lg: 'flex'}, flex: '1', minW: '0'}}
          />
          <HStack
            display={{base: 'none', md: 'flex'}}
            alignItems='stretch'
            flex={{md: '1', xl: 'none'}}
            flexShrink={0}
            w={{xl: '350px', '2xl': '400px'}}
            h='100%'
            spacing={0.5}
          >
            {/* Lien vers les promotions si dispo, sinon vers les produits indispensables */}
            {(itemResultsPromo?.hits ?? itemResultsMP) && (
              <Box
                display={{base: 'none', lg: 'flex'}}
                flex='1'
              >
                <NavButton
                  ButtonIcon={promoDispo ? PromotionIcon : StarIcon}
                  label={buttonText}
                  to={Lien}
                  color={promoDispo ? 'red.500' : 'blue.main'}
                  textColor='blue.text'
                />
              </Box>
            )}
            {/* Lien vers le cadencier (liste des produits) */}
            <NavButton
              ButtonIcon={ListIcon}
              label={t('liste.mes_produits')}
              to={buildClientRoute(routes.client.cadencier)}
            />
            {/* Paniers actifs */}
            <NavButton
              showAs={Box}
              ButtonIcon={PaniersBadge}
              label={paniersLabel}
              onClick={() => {
                NiceModal.show(ListePaniersModale);
              }}
            />
          </HStack>
          {/* Paniers actifs — visible uniquement en mobile */}
          <Box
            display={{base: 'flex', md: 'none'}}
            alignItems='center'
            w='100%'
          >
            <NavButton
              showAs={Box}
              ButtonIcon={PaniersBadge}
              label={paniersLabel}
              onClick={() => {
                NiceModal.show(ListePaniersModale);
              }}
            />
          </Box>
        </HStack>
        {/* Séparateur + CartButton — largeur fixe alignée avec le side panel */}
        <Box
          borderLeft='1px solid'
          borderColor='gray.light'
          alignSelf='stretch'
          my={{md: '-3'}}
        />
        <CartButton />
      </HStack>
    </VStack>
  );
};
